import Vue from 'vue'
import i18n from '@/commons/translations/i18n'
import { ObjectUtils } from '@/commons/utils/objects'
import { ImageUtils } from '@/commons/utils/image'
import {
    min,
    max,
    mimes,
    numeric,
    required
} from 'vee-validate/dist/rules'
import { ValidationProvider, extend } from 'vee-validate'
import { ValidationRuleSchema } from 'vee-validate/dist/types/types'
import {
    ImageDimensions,
    ValidationError
} from '@/commons/types'

export function translateValidationError (error: ValidationError | string): string {
    if (typeof error === 'string') {
        return i18n.t(error).toString()
    }
    return i18n.t(error.templateKey, !ObjectUtils.isEmpty(error.params) ? error.params : {}).toString()
}

const REQUIRED_SCHEMA = {
    ...required,
    message: (field: string): ValidationError => {
        let translationReference = 'default'
        if (field !== '{field}') {
            translationReference = field
        }
        return {
            templateKey: `validations.required.${translationReference}`
        }
    }
}

const EMAIL_SCHEMA = {
    validate: function (value: string): boolean {
        const pattern = /^([\w!#$%&'*+-/=?^`{|}~]+(\.[\w!#$%&'*+-/=?^`{|}~]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g
        return pattern.test(value)
    },
    message: (): ValidationError => (
        {
            templateKey: 'validations.email.default'
        }
    )
}

const NUMERIC_SCHEMA = {
    ...numeric,
    message: (): ValidationError => ({
        templateKey: 'validations.numeric.default'
    })
}

const MIN_SCHEMA = {
    ...min,
    message: (field: string, params?: Record<string, string | number>): ValidationError => {
        const minLength = params && params.length ? params.length : 0
        return {
            templateKey: 'validations.min.default',
            params: {
                number: minLength
            }
        }
    }
}

const MAX_SCHEMA = {
    ...max,
    message: (field: string, params?: Record<string, string | number>): ValidationError => {
        const maxLength = params && params.length ? params.length : 0
        return {
            templateKey: 'validations.max.default',
            params: {
                number: maxLength
            }
        }
    }
}

const PHONE_NUMBER_SCHEMA = {
    params: ['__validationError__'],
    validate: function (value: string, params: Record<string, object>): boolean {
        if (!/^[+0-9]/g.test(value)) {
            params.__validationError__ = {
                templateKey: 'validations.phoneNumber.beginning'
            }
            return false
        }

        if (!/[0-9]$/g.test(value)) {
            params.__validationError__ = {
                templateKey: 'validations.phoneNumber.ending'
            }
            return false
        }

        if (!/^.([ ]?[0-9]+)*$/g.test(value)) {
            params.__validationError__ = {
                templateKey: 'validations.phoneNumber.middle'
            }
            return false
        }

        if (value.replace(/[^0-9]/g, '').length < 7) {
            params.__validationError__ = {
                templateKey: 'validations.phoneNumber.min'
            }
            return false
        }

        return true
    },
    message: function (_: string, { __validationError__ }: Record<string, object | string>): ValidationError {
        return __validationError__ as ValidationError
    }
}

const ADDRESS_SCHEMA = {
    validate: function (value: string): boolean {
        const pattern = /^[\wäÄöÖüÜõÕžŽšŠ @.!?,'+-/]+$/g
        return pattern.test(value)
    },
    message: (): ValidationError => ({
        templateKey: 'validations.address.default'
    })
}

const MIME_SCHEMA = {
    ...mimes,
    message: (field: string, params?: Record<string, string>): ValidationError => {
        let fileTypes = ''
        let translationReference = 'default'
        if (field !== '{field}') {
            translationReference = field
        }
        if (params) {
            for (const [key, val] of Object.entries(params)) {
                if (!isNaN(Number(key))) {
                    const fileType = `.${val.split('/')[1]}`
                    fileTypes += fileTypes ? `, ${fileType}` : fileType
                }
            }
        }
        return {
            templateKey: `validations.mimes.${translationReference}`,
            params: {
                type: fileTypes
            }
        }
    }
}

const IMAGE_SIZE_SCHEMA = {
    params: [
        'minBinary',
        'maxBinary',
        'minText',
        'maxText',
        '__validationError__'
    ],
    validate: function (value: File, params: Record<string, string | undefined | object>): boolean {
        const validationErrorPrefix = 'validations.image'
        const minBinary: number = params && params.minBinary ? Number(params.minBinary) : 0
        const maxBinary: number = params && params.maxBinary ? Number(params.maxBinary) : 0
        const minText: string = params && params.minText ? params.minText.toString() : ''
        const maxText: string = params && params.maxText ? params.maxText.toString() : ''
        const uploadedImageSize = ImageUtils.getImageSizeInMb(value.size).toFixed(2)

        if (value.size < minBinary) {
            params.__validationError__ = {
                templateKey: `${validationErrorPrefix}.minSizeError`,
                params: {
                    size: minText,
                    uploadedSize: uploadedImageSize
                }
            }
            return false
        } else if (value.size > maxBinary) {
            params.__validationError__ = {
                templateKey: `${validationErrorPrefix}.maxSizeError`,
                params: {
                    size: maxText,
                    uploadedSize: uploadedImageSize
                }
            }
            return false
        }
        return true
    },
    message: function (_: string, { __validationError__ }: Record<string, object | string>): ValidationError {
        return __validationError__ as ValidationError
    }
}

const MIN_DIMENSIONS_SCHEMA = {
    params: ['width', 'height', '__validationError__'],
    validate: async (value: File, params: Record<string, object | string | number>): Promise<boolean> => {
        const validationErrorPrefix = 'validations.image'

        if (!/\.(jpg|svg|jpeg|png|bmp|gif)$/i.test(value.name)) {
            params.__validationError__ = {
                templateKey: `${validationErrorPrefix}.invalidFile`
            }
            return false
        }

        const arrayBuffer = await ImageUtils.fileToArrayBuffer(value)
        const imageStartBytes = new Uint8Array(arrayBuffer as ArrayBuffer, 0, 2)

        if (!ImageUtils.isJpegImage((imageStartBytes))) {
            params.__validationError__ = {
                templateKey: `${validationErrorPrefix}.not.jpeg`
            }
            return false
        }

        try {
            const dimensions: ImageDimensions = await ImageUtils.getImageDimensions(value)
            const requiredWidth = params && params.width ? params.width : 0
            const requiredHeight = params && params.height ? params.height : 0
            if (dimensions.width < requiredWidth) {
                params.__validationError__ = {
                    templateKey: `${validationErrorPrefix}.minWidthError`,
                    params: {
                        width: requiredWidth,
                        uploadedWidth: dimensions.width
                    }
                }
                return false
            } else if (dimensions.height < requiredHeight) {
                params.__validationError__ = {
                    templateKey: `${validationErrorPrefix}.minHeightError`,
                    params: {
                        height: requiredHeight,
                        uploadedHeight: dimensions.height
                    }
                }
                return false
            }
            return true
        } catch (e) {
            params.__validationError__ = {
                templateKey: `${validationErrorPrefix}.invalid`
            }
            return false
        }
    },
    message: function (_: string, { __validationError__ }: Record<string, object | string>): ValidationError {
        return __validationError__ as ValidationError
    }
}

const TERMS_AND_CONDITIONS_SCHEMA = {
    validate: function (value: boolean): boolean {
        return value
    },
    message: function (): ValidationError {
        return {
            templateKey: 'validations.termsAndConditions'
        }
    }
}

const ADS_ADDRESS_VALIDATION_SCHEMA = {
    params: ['adsId'],
    validate: function (value: string, params: Record<string, number>): boolean {
        return !!(value ? params.adsId && params.adsId > 0 : true)
    },
    message: function (): ValidationError {
        return {
            templateKey: 'warning.notInAds'
        }
    }
}

const NOTES_SCHEMA = {
    validate: function (value: string): boolean {
        const pattern = /^[\wäÄöÖüÜõÕžŽšŠ @.!?,'+-/\n]+$/g
        return pattern.test(value)
    },
    message: (): ValidationError => ({
        templateKey: 'validations.notes.default'
    })
}

extend('min', MIN_SCHEMA as unknown as ValidationRuleSchema)
extend('max', MAX_SCHEMA as unknown as ValidationRuleSchema)
extend('mimes', MIME_SCHEMA as unknown as ValidationRuleSchema)
extend('email', EMAIL_SCHEMA as unknown as ValidationRuleSchema)
extend('address', ADDRESS_SCHEMA as unknown as ValidationRuleSchema)
extend('numeric', NUMERIC_SCHEMA as unknown as ValidationRuleSchema)
extend('required', REQUIRED_SCHEMA as unknown as ValidationRuleSchema)
extend('imageSize', IMAGE_SIZE_SCHEMA as unknown as ValidationRuleSchema)
extend('minDimensions', MIN_DIMENSIONS_SCHEMA as unknown as ValidationRuleSchema)
extend('phoneNumber', PHONE_NUMBER_SCHEMA as unknown as ValidationRuleSchema)
extend('adsAddress', ADS_ADDRESS_VALIDATION_SCHEMA as unknown as ValidationRuleSchema)
extend('termsAndConditions', TERMS_AND_CONDITIONS_SCHEMA as unknown as ValidationRuleSchema)
extend('notes', NOTES_SCHEMA as unknown as ValidationRuleSchema)

Vue.component('validation-provider', ValidationProvider)
